import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, ShieldAlert } from 'lucide-react';
import { InvoiceStatusBadge } from '@/components/insurance/InvoiceStatusBadge';

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string): string {
  return `${currency} ${amount.toFixed(2)}`;
}

function InvoiceTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center px-6 py-3 border-b border-table-row-border gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
      ))}
    </>
  );
}

export function CompanyInvoicesListPage() {
  const navigate = useNavigate();
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const { canViewInvoices } = useCompanyRole();
  const [searchQuery, setSearchQuery] = useState('');

  const rawInvoices = useQuery(
    api.invoices.queries.listByCompany,
    currentCompany && canViewInvoices ? { companyId: currentCompany._id } : 'skip'
  );

  const isLoading = companyLoading || rawInvoices === undefined;

  const filtered = useMemo(() => {
    const list = rawInvoices ?? [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(inv => inv.invoiceNumber.toLowerCase().includes(q));
  }, [rawInvoices, searchQuery]);

  if (!companyLoading && !canViewInvoices) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShieldAlert className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">Access Denied</h3>
          <p className="text-sm text-muted-foreground">
            Only admin and accountant roles can view invoices.
          </p>
        </div>
      </div>
    );
  }

  if (!companyLoading && !currentCompany) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No company found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of a company to view invoices.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${rawInvoices?.length ?? 0} total`}
          </p>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-2xl overflow-hidden shadow">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="invoice-search" className="sr-only">
            Search invoices
          </label>
          <Input
            id="invoice-search"
            placeholder="Search by invoice #..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
        </div>

        <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          <span className="w-32 shrink-0">Invoice #</span>
          <span className="w-32 shrink-0">Total</span>
          <span className="w-28 shrink-0">Status</span>
          <span className="w-32 shrink-0">Due Date</span>
          <span className="flex-1">Created</span>
        </div>

        <div className="overflow-auto">
          {isLoading && <InvoiceTableSkeleton />}

          {!isLoading &&
            filtered.map(invoice => (
              <div
                key={invoice._id}
                className="flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate(`/company/invoices/${invoice._id}`)}
              >
                <span className="w-32 shrink-0 text-sm font-medium text-foreground truncate pr-3">
                  {invoice.invoiceNumber}
                </span>
                <span className="w-32 shrink-0 text-sm text-foreground font-mono pr-3">
                  {formatCurrency(invoice.total, invoice.currency)}
                </span>
                <span className="w-28 shrink-0">
                  <InvoiceStatusBadge status={invoice.status} />
                </span>
                <span className="w-32 shrink-0 text-sm text-muted-foreground">
                  {invoice.dueDate ? formatDate(invoice.dueDate) : '—'}
                </span>
                <span className="flex-1 text-sm text-muted-foreground">
                  {formatDate(invoice.createdAt)}
                </span>
              </div>
            ))}

          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No invoices found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'No invoices have been sent to your company yet'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
